import { createDefaultInstrumentPatch, normalizeInstrumentPatch } from './patchSchema';
import { HOSTED_INSTRUMENTS, HOSTED_LIBRARIES } from './hostedLibraries';

/** A playable patch around one CookUp-hosted multisampled instrument. */
export function hostedInstrumentPatch({ library, instrument, definition }) {
  const base = createDefaultInstrumentPatch();
  const patch = normalizeInstrumentPatch({
    ...base,
    id: `hosted-${library}-${instrument}`,
    name: definition.name,
    category: definition.category,
    tags: [...definition.tags, 'Hosted'],
    credit: HOSTED_LIBRARIES[library].credit,
    layers: base.layers.map((layer, index) => (index === 0 ? {
      ...layer,
      engine: 'sample',
      name: definition.name,
      hostedInstrument: { library, instrument },
      sampleZones: [],
      sampleMode: 'gate',
      level: 0.85,
      enabled: true,
      octave: 0,
      semitone: 0,
      detune: 0,
      unison: 1,
      // Real velocity layers already carry the dynamics and the round robins the variation.
      velocityTone: 0,
      humanize: definition.humanize ?? 0.25
    } : { ...layer, enabled: false, level: 0 })),
    ampEnvelope: { ...base.ampEnvelope, attack: 0.002, decay: 0.1, sustain: 1, release: definition.release, velocity: 0 },
    filter: { ...base.filter, enabled: false },
    filter2: { ...base.filter2, enabled: false },
    lfo: { ...base.lfo, enabled: false },
    lfo2: { ...base.lfo2, enabled: false },
    macros: Object.fromEntries(Object.keys(base.macros).map((name) => [name, 0])),
    effects: {
      ...base.effects,
      distortion: { ...base.effects.distortion, enabled: false, mix: 0 },
      chorus: { ...base.effects.chorus, enabled: false, mix: 0 },
      delay: { ...base.effects.delay, enabled: false, mix: 0 },
      reverb: { ...base.effects.reverb, enabled: definition.reverb > 0, mix: definition.reverb, size: 0.55 },
      compressor: { ...base.effects.compressor, enabled: false }
    },
    performance: { ...base.performance, voices: 32, glide: 0, mono: false, legato: false }
  });
  return definition.soundProfile ? { ...patch, soundProfile: definition.soundProfile } : patch;
}

export const HOSTED_INSTRUMENT_PRESETS = HOSTED_INSTRUMENTS.map(hostedInstrumentPatch);

/** The hosted presets of the given (available) libraries. */
export const availableHostedPresets = (libraryIds = []) => {
  const ids = new Set(libraryIds);
  return HOSTED_INSTRUMENT_PRESETS.filter((preset) => ids.has(preset.layers[0].hostedInstrument?.library));
};
